import React, { memo, useId, useMemo } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { green, red } from '../../assets/colors'
import {
    TechnicalActionCounts,
    TechnicalRatingLabel,
    isBearishLabel,
    isBullishLabel
} from '../../util/technicals'

const GAUGE_ANIMATION_MS = 480
const GAUGE_EASING = 'cubic-bezier(0.215, 0.61, 0.355, 1)'

type TechnicalsSummaryCardProps = {
    title: string
    label: TechnicalRatingLabel
    score: number
    counts: TechnicalActionCounts
    emphasized?: boolean
    showTitle?: boolean
    showCounts?: boolean
    /** Sweep the needle to the new score. Off for the compact overview gauge. */
    animated?: boolean
}

/**
 * SVG geometry — pivot/baseline sits on CY near the bottom of the viewBox
 * so the semicircle rises upward from a shared horizontal base.
 */
const VIEW_W = 200
const VIEW_H = 104
const CX = 100
const CY = 100
const RADIUS = 78
const STROKE = 8
const NEEDLE_LEN = 56

const useStyles = makeStyles()((theme) => ({
    root: {
        minWidth: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    title: {
        fontSize: 11,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'center',
        marginBottom: theme.spacing(0.25),
        flexShrink: 0
    },
    titleEmphasized: {
        fontSize: 13,
        fontWeight: 500
    },
    /** Only absorbs the height difference vs Summary so baselines stay aligned. */
    spacer: {
        flex: '1 1 0',
        minHeight: 0,
        maxHeight: 40
    },
    gaugeBlock: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch'
    },
    gaugeWrap: {
        width: '100%',
        maxWidth: 180,
        margin: '0 auto'
    },
    gaugeWrapEmphasized: {
        maxWidth: 240
    },
    svg: {
        display: 'block',
        width: '100%',
        height: 'auto',
        overflow: 'visible'
    },
    label: {
        fontSize: 16,
        fontWeight: 400,
        textAlign: 'center',
        lineHeight: 1.2,
        marginTop: theme.spacing(1.25),
        marginBottom: theme.spacing(1.5)
    },
    labelEmphasized: {
        fontSize: 20
    },
    countsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: theme.spacing(1)
    },
    countCol: {
        flex: 1,
        textAlign: 'center',
        minWidth: 0
    },
    countCaption: {
        fontSize: 13,
        fontWeight: 400,
        color: theme.palette.text.secondary,
        lineHeight: 1.2,
        marginBottom: theme.spacing(0.25)
    },
    countValue: {
        fontSize: 15.5,
        fontWeight: 400,
        color: theme.palette.text.primary,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.2,
        transition: `opacity ${GAUGE_ANIMATION_MS}ms ease-out`
    },
    countValueEmphasized: {
        fontSize: 17
    }
}))

const labelColor = (label: TechnicalRatingLabel, neutralColor: string) => {
    if (isBullishLabel(label)) return green
    if (isBearishLabel(label)) return red
    return neutralColor
}

/** Upper semicircle, left tip → right tip. */
const ARC_PATH = `M ${CX - RADIUS} ${CY} A ${RADIUS} ${RADIUS} 0 0 1 ${CX + RADIUS} ${CY}`
const ARC_LENGTH = Math.PI * RADIUS

const clampScore = (score: number): number => Math.max(-1, Math.min(1, score))

const TechnicalsSummaryCard = ({
    title,
    label,
    score,
    counts,
    emphasized = false,
    showTitle = true,
    showCounts = true,
    animated = true
}: TechnicalsSummaryCardProps) => {
    const { classes, cx } = useStyles()
    const theme = useTheme()
    const gradientId = `technicals-gauge-${useId().replace(/:/g, '')}`

    // Animated by the compositor via CSS so the needle never re-renders React per frame.
    const clampedScore = clampScore(score)
    const needleRotation = clampedScore * 90
    const fillOffset = (ARC_LENGTH * (1 - clampedScore)) / 2
    const transitionFor = (property: string) =>
        animated ? `${property} ${GAUGE_ANIMATION_MS}ms ${GAUGE_EASING}` : 'none'

    const gradientStops = useMemo(
        () => [
            { offset: '0%', color: '#b71c1c' },
            { offset: '18%', color: red },
            { offset: '36%', color: '#ff8a65' },
            { offset: '48%', color: theme.palette.grey[500] },
            { offset: '52%', color: theme.palette.grey[500] },
            { offset: '64%', color: '#4db6ac' },
            { offset: '82%', color: green },
            { offset: '100%', color: '#00695c' }
        ],
        [theme]
    )

    return (
        <Box className={classes.root}>
            {showTitle && (
                <Typography className={cx(classes.title, emphasized && classes.titleEmphasized)}>{title}</Typography>
            )}

            <Box className={classes.spacer} />

            <Box className={classes.gaugeBlock}>
                <Box className={cx(classes.gaugeWrap, emphasized && classes.gaugeWrapEmphasized)}>
                    <svg className={classes.svg} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} aria-hidden>
                        <defs>
                            <linearGradient
                                id={gradientId}
                                gradientUnits="userSpaceOnUse"
                                x1={CX - RADIUS}
                                y1={CY}
                                x2={CX + RADIUS}
                                y2={CY}
                            >
                                {gradientStops.map((stop) => (
                                    <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
                                ))}
                            </linearGradient>
                        </defs>
                        {/* Gray track — full semicircle, flat butt ends on the baseline */}
                        <path
                            d={ARC_PATH}
                            fill="none"
                            stroke={theme.palette.grey[700]}
                            strokeWidth={STROKE}
                            strokeLinecap="butt"
                        />
                        {/* Colored fill from left (Strong Sell) up to the needle, revealed by dash offset */}
                        <path
                            d={ARC_PATH}
                            fill="none"
                            stroke={`url(#${gradientId})`}
                            strokeWidth={STROKE}
                            strokeLinecap="butt"
                            strokeDasharray={ARC_LENGTH}
                            strokeDashoffset={fillOffset}
                            style={{ transition: transitionFor('stroke-dashoffset') }}
                        />
                        <g
                            style={{
                                transformBox: 'view-box',
                                transformOrigin: `${CX}px ${CY}px`,
                                transform: `rotate(${needleRotation}deg)`,
                                transition: transitionFor('transform')
                            }}
                        >
                            <line
                                x1={CX}
                                y1={CY}
                                x2={CX}
                                y2={CY - NEEDLE_LEN}
                                stroke={theme.palette.text.primary}
                                strokeWidth={2.25}
                                strokeLinecap="round"
                            />
                            <circle
                                cx={CX}
                                cy={CY}
                                r={4}
                                fill={theme.palette.background.paper}
                                stroke={theme.palette.text.primary}
                                strokeWidth={2}
                            />
                        </g>
                    </svg>
                </Box>

                <Typography
                    className={cx(classes.label, emphasized && classes.labelEmphasized)}
                    sx={{
                        color: labelColor(label, theme.palette.text.secondary),
                        transition: transitionFor('color')
                    }}
                >
                    {label}
                </Typography>

                {showCounts && (
                    <Box className={classes.countsRow}>
                        <Box className={classes.countCol}>
                            <Typography className={classes.countCaption}>Sell</Typography>
                            <Typography className={cx(classes.countValue, emphasized && classes.countValueEmphasized)}>
                                {counts.sell}
                            </Typography>
                        </Box>
                        <Box className={classes.countCol}>
                            <Typography className={classes.countCaption}>Neutral</Typography>
                            <Typography className={cx(classes.countValue, emphasized && classes.countValueEmphasized)}>
                                {counts.neutral}
                            </Typography>
                        </Box>
                        <Box className={classes.countCol}>
                            <Typography className={classes.countCaption}>Buy</Typography>
                            <Typography className={cx(classes.countValue, emphasized && classes.countValueEmphasized)}>
                                {counts.buy}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default memo(TechnicalsSummaryCard)
